import { PICK_FRAMES_PER_OP } from "../assets/budget.ts";
import type { Command } from "../document/commands.ts";
import { framesWithout } from "../document/tileset-frames.ts";
import { distinctOf, hasOnly, isText, tilesetTilesOf, type Fields } from "./op-read.ts";
import { OP_TILESET_IDS_MAX, type Intent, type TilesetTiles } from "./op-types.ts";

/**
 * The `changeTilesets` intent on the wire (ADR-086 note 20, ADR-129 decision 3.8): how it is read, and the
 * intents a tileset command sends. `add` and `remove` name whole tilesets; `pull` and `drop` name tiles of a
 * tileset the world holds, or will hold, a partial entry of.
 */

/**
 * Whether a value is a tileset's id: a reference that is not blank.
 *
 * @param item The value.
 * @returns True for a string of 1 to `OP_TEXT_MAX` code units.
 */
function isTileset(item: unknown): item is string {
  return isText(item, 1);
}

/**
 * The length of a value when it is a list.
 *
 * @param value The value.
 * @returns Its length, or 0 for anything else.
 */
function lengthOf(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/**
 * Reads a `changeTilesets` intent. `add` and `remove` are both present, each a list of distinct tileset ids.
 * `pull` and `drop` are each absent or a list of at least one `{ id, tiles }`, so an intent has one encoding.
 * An id stands once in one of the four lists and in no other, so nothing in an intent depends on the order its
 * lists are read in. The tiles of both lists together number at most `PICK_FRAMES_PER_OP`. The four lists are
 * not all empty. Every length is checked before a list is read: `add` and `remove` together name at most
 * `OP_TILESET_IDS_MAX` ids, and `pull` and `drop` each hold at most `PICK_FRAMES_PER_OP` entries.
 *
 * @param fields The intent's fields.
 * @returns The intent rebuilt as `k`, `add`, `remove`, then `pull` and `drop` when present, or undefined when
 *   it is not exactly one.
 */
export function changeTilesetsOf(fields: Fields): Intent | undefined {
  if (!hasOnly(fields, ["k", "add", "remove"], ["pull", "drop"]) || lengthOf(fields["add"]) + lengthOf(fields["remove"]) > OP_TILESET_IDS_MAX) {
    return undefined;
  }
  const add = distinctOf(fields["add"], isTileset);
  const remove = distinctOf(fields["remove"], isTileset);
  const pull = fields["pull"] === undefined ? null : tilesetTilesOf(fields["pull"]);
  const drop = fields["drop"] === undefined ? null : tilesetTilesOf(fields["drop"]);
  if (add === undefined || remove === undefined || pull === undefined || drop === undefined) {
    return undefined;
  }
  const picked = [...(pull ?? []), ...(drop ?? [])];
  const ids = [...add, ...remove, ...picked.map((entry) => entry.id)];
  if (ids.length === 0 || new Set(ids).size !== ids.length || picked.reduce((sum, entry) => sum + entry.tiles.length, 0) > PICK_FRAMES_PER_OP) {
    return undefined;
  }
  return { k: "changeTilesets", add, remove, ...(pull === null ? {} : { pull }), ...(drop === null ? {} : { drop }) };
}

/**
 * Cuts lists of tiles into runs of intents none of which names more than `PICK_FRAMES_PER_OP` tiles, keeping
 * the entries' order and each entry's tiles ascending; an entry that does not fit what is left of an intent
 * carries on in the next.
 *
 * @param entries The entries.
 * @returns The entries of each intent, in order; none when there are no entries.
 */
function tileRuns(entries: readonly TilesetTiles[]): TilesetTiles[][] {
  const runs: TilesetTiles[][] = [];
  let run: TilesetTiles[] = [];
  let room = PICK_FRAMES_PER_OP;
  for (const entry of entries) {
    let from = 0;
    while (from < entry.tiles.length) {
      if (room === 0) {
        runs.push(run);
        run = [];
        room = PICK_FRAMES_PER_OP;
      }
      const tiles = entry.tiles.slice(from, from + room);
      run.push({ id: entry.id, tiles });
      from += tiles.length;
      room -= tiles.length;
    }
  }
  if (run.length > 0) {
    runs.push(run);
  }
  return runs;
}

/**
 * The intents a tileset command sends (ADR-129 decision 3.8, "What a command sends"). Each removed whole entry
 * is a `remove` intent of its own. Each removed partial entry and each narrowed entry is a `drop` of its own,
 * naming the tiles that went, so another builder's pull of the same tileset made in between is not taken with
 * it. Every addition travels together: whole inserts and entries made whole in `add`, partial inserts and
 * widened entries in `pull` with the tiles that were added, as one intent whenever they number at most
 * `PICK_FRAMES_PER_OP` tiles, which every pull made in the editor does. A longer list of tiles, as the removal
 * of a large partial entry or its undo holds, is cut into consecutive intents.
 *
 * @param command The command.
 * @returns The intents, removals and drops first.
 * @throws Error for a command that sets a whole entry's frames, which has no intent: only the local inverse
 *   of an Add over a pull makes one, and the stream never sends an inverse.
 */
export function tilesetIntents(command: Extract<Command, { readonly kind: "changeTilesets" }>): Intent[] {
  const out: Intent[] = [];
  /**
   * Appends a drop of one tileset's tiles, in as many intents as they need.
   *
   * @param id The tileset.
   * @param tiles The tiles that went.
   */
  const drop = (id: string, tiles: readonly number[]): void => {
    for (const run of tileRuns([{ id, tiles }])) {
      out.push({ k: "changeTilesets", add: [], remove: [], drop: run });
    }
  };
  for (const entry of command.remove) {
    if (entry.frames === undefined) {
      out.push({ k: "changeTilesets", add: [], remove: [entry.id] });
    } else {
      drop(entry.id, entry.frames);
    }
  }
  const add: string[] = [];
  const pull: TilesetTiles[] = [];
  for (const change of command.frames ?? []) {
    if (change.before === undefined) {
      throw new Error(`tileset ${change.id} goes from whole to named frames, which no operation carries`);
    }
    if (change.after === undefined) {
      add.push(change.id);
      continue;
    }
    const went = framesWithout(change.before, change.after);
    const came = framesWithout(change.after, change.before);
    if (went.length > 0) {
      drop(change.id, went);
    }
    if (came.length > 0) {
      pull.push({ id: change.id, tiles: came });
    }
  }
  for (const entry of command.insert) {
    if (entry.frames === undefined) {
      add.push(entry.id);
    } else {
      pull.push({ id: entry.id, tiles: entry.frames });
    }
  }
  const runs = tileRuns(pull);
  if (add.length > 0 || runs.length > 0) {
    out.push({ k: "changeTilesets", add, remove: [], ...(runs[0] === undefined ? {} : { pull: runs[0] }) });
  }
  for (const run of runs.slice(1)) {
    out.push({ k: "changeTilesets", add: [], remove: [], pull: run });
  }
  return out;
}
